import { TablePagination } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import {
  DeleteIcon,
  EditIcon,
  PlusIcon,
  WarningIcon,
} from "assets/images/common";
import { BasicTable, Button, PageTitle, Text, Warning } from "components";
import ConfirmModal from "components/Feature/ConfirmModal";
import { PATH } from "constants/app.const";
import { useAppDispatch, useAppNavigate, useAppSelector } from "hooks/useHook";
import { StatusIndicator, TBD } from "models/common.model";
import { selectFundList, selectFundLoading } from "store/fund/fund.selector";
import { fundActions } from "store/fund/fund.slice";
import {
  selectOrganisation,
  selectOrganisationProgress,
} from "store/organisation/organisation.selector";
import { StatusFlag } from "../components";

const CONFIG = [
  {
    name: "Fund name",
    width: "auto",
  },
  {
    name: "Status",
    width: 160,
  },
  {
    name: "Action",
    width: 240,
  },
];

export const CompanyFundsPage = () => {
  const [tableData, setTableData] = useState<TBD<unknown>[]>([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [deleteFundId, setDeleteFundId] = useState<string | undefined>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const organisationProgress = useAppSelector(selectOrganisationProgress);
  const fundLoading = useAppSelector(selectFundLoading);
  const { id, message, status } = useAppSelector(selectOrganisation);
  const fundList = useAppSelector(selectFundList);

  const listData = useMemo(() => {
    return fundList;
  }, [fundList]);

  const handleCreateFund = () => {
    dispatch(
      fundActions.createFund({
        organisationId: id,
        callback: (fundId) => navigate(`${PATH.COMPANY_FUNDS}/${fundId}`),
      })
    );
  };

  const handleDeleteFund = () => {
    if (deleteFundId) {
      dispatch(
        fundActions.deleteFund({
          fundId: deleteFundId,
          organisationId: id,
          callback: () => {
            setDeleteFundId(undefined);
          },
        })
      );
    }
  };

  useEffect(() => {
    if (organisationProgress.length) {
      setIsOpenModal(!!organisationProgress.find((item) => !item.isComplete));
    }
  }, [organisationProgress]);

  useEffect(() => {
    dispatch(fundActions.getFundList({ organisationId: id }));
  }, [dispatch, id]);

  useEffect(() => {
    const data = listData.map((item) => {
      return {
        fundName: <GroupCell>{item.displayName}</GroupCell>,
        status: (
          <GroupCell>
            <StatusFlag status={item.status} />
          </GroupCell>
        ),
        action: (
          <GroupCell>
            <ActionWrapper
              onClick={() => navigate(`${PATH.COMPANY_FUNDS}/${item.id}`)}
            >
              <EditIcon />
              <Text size="md">View more</Text>
            </ActionWrapper>

            <ActionWrapper onClick={() => setDeleteFundId(item.id)}>
              <DeleteIcon />
              <Text size="md">Delete</Text>
            </ActionWrapper>
          </GroupCell>
        ),
      };
    });

    setTableData(data);
  }, [id, listData, navigate]);

  return (
    <Wrapper>
      <Header>
        <PageTitle>Funds</PageTitle>

        <Button
          icon={<PlusIcon color="#FFFFFF" />}
          onClick={handleCreateFund}
          loading={fundLoading}
        >
          Create a new fund
        </Button>
      </Header>

      {status === StatusIndicator.FLAGGED && (
        <Warning
          icon={<WarningIcon />}
          text={`Your Company request was rejected by the moderator.${
            message ? ` "${message}"` : ""
          }`}
          backgroundColor="#fff1f0"
          color="#b4453d"
          hasButton
          buttonText="Complete Settings"
          handleClickButton={() => navigate(PATH.COMPANY_SETTINGS)}
        />
      )}

      <BasicTable config={CONFIG} data={tableData} />

      {listData.length > rowsPerPage && (
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={listData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
        />
      )}

      <ConfirmModal
        title="Fill out Company Settings"
        subTitle="Fill out Company Settings to open your fund"
        confirmButtonText="Complete Settings"
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        callback={() => navigate(PATH.COMPANY_SETTINGS)}
      />

      <ConfirmModal
        title="Are you sure to delete this fund?"
        isOpen={!!deleteFundId}
        onClose={() => setDeleteFundId(undefined)}
        callback={handleDeleteFund}
        loading={fundLoading}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-height: 100%;
  padding: 32px;

  ${(props) => props.theme.breakpoints.down("sm")} {
    padding: 20px;
  }

  & > .MuiPaper-root {
    flex: 1;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;

  .button-wrapper button {
    height: 40px;
    margin-left: auto;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const GroupCell = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  color: ${(props) => props.theme.palette.primaryText.light};
  font-size: 16px;
`;

const ActionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;

  svg {
    width: 20px;
    height: auto;
  }
  &:hover {
    text-decoration: underline;
  }
`;
